import { tone } from "../audio";

/**
 * Binds the on-screen recording controls to a SessionRecorder.
 *
 * The buttons are the primary interface, not a convenience over the keyboard
 * shortcuts: the intended setup is a tablet on a tripod at the side of the gym,
 * where there is no keyboard to press. The shortcuts stay bound for laptop use.
 */
export class RecorderControls {
  /**
   * @param recorder The SessionRecorder to drive.
   * @param handlers.onStart Must return the capture metadata for the session.
   * @param handlers.onStop Called after recording stops, to save the session.
   * @param handlers.countdownSeconds Delay before recording begins. 0 starts
   *   immediately.
   */
  constructor(recorder, { onStart, onStop, countdownSeconds = 0 }) {
    this.recorder = recorder;
    this.onStart = onStart;
    this.onStop = onStop;
    this.countdownSeconds = countdownSeconds;
    this.remaining = 0;
    this.ticker = null;

    this.countdown = document.getElementById("countdown");
    this.countdownNumber = document.getElementById("countdown-number");
    this.indicator = document.getElementById("rec-indicator");
    this.indicatorTime = document.getElementById("rec-time");

    this.toggleButton = document.getElementById("record-toggle");
    this.markButton = document.getElementById("mark-rep");
    this.status = document.getElementById("control-status");

    this.toggleButton.addEventListener("click", () => this.toggle());
    this.markButton.addEventListener("click", () => this.mark());

    window.addEventListener("keydown", (event) => {
      if (event.repeat || isTypingTarget(event.target)) {
        return;
      }
      if (event.key.toLowerCase() === "r") {
        this.toggle();
      } else if (event.code === "Space") {
        // Space would otherwise re-trigger whichever button has focus.
        event.preventDefault();
        this.mark();
      }
    });

    this.render();
  }

  get countingDown() {
    return this.ticker != null;
  }

  toggle() {
    if (this.recorder.recording) {
      this.recorder.stop();
      this.onStop();
    } else if (this.countingDown) {
      this.cancelCountdown();
    } else if (this.countdownSeconds > 0) {
      this.startCountdown();
    } else {
      this.begin();
    }
    this.render();
  }

  /**
   * Starts a recording if one is not already starting or running.
   *
   * Separate from `toggle()` because the two mean opposite things when a
   * countdown is in flight: toggling then is the Cancel the button offers,
   * while arming again is the caller repeating itself. `?record=1` arms from
   * the render loop, which runs sixty times a second, so a caller that could
   * only toggle took the countdown down and put it back up on alternating
   * frames.
   */
  arm() {
    if (this.recorder.recording || this.countingDown) {
      return;
    }
    this.toggle();
  }

  /**
   * Counts down before recording, so the phone can be propped up and the
   * athlete can walk into frame. Each tick beeps: facing away from the screen,
   * sound is the only channel left.
   */
  startCountdown() {
    this.remaining = this.countdownSeconds;
    tone(660, 0.08);
    this.ticker = setInterval(() => {
      this.remaining -= 1;
      if (this.remaining > 0) {
        tone(660, 0.08);
        this.render();
        return;
      }
      this.clearTicker();
      this.begin();
      this.render();
    }, 1000);
  }

  cancelCountdown() {
    this.clearTicker();
    this.remaining = 0;
  }

  clearTicker() {
    if (this.ticker != null) {
      clearInterval(this.ticker);
      this.ticker = null;
    }
  }

  begin() {
    // Wall clock, deliberately not the recorder's frame span. Frames only
    // accumulate while a pose is detected, so a frame-derived timer sits at
    // 0:00 while the athlete walks into shot — which reads as "not recording"
    // at the exact moment the indicator most needs to be believable.
    this.startedAt = performance.now();
    this.recorder.start(this.onStart());
    // Distinct from the tick: a longer, higher tone means "now".
    tone(1040, 0.35);
  }

  mark() {
    if (!this.recorder.recording) {
      return;
    }
    this.recorder.mark();
    this.render();
  }

  /** Called every frame while recording so the counters stay live. */
  render() {
    const recording = this.recorder.recording;
    const counting = this.countingDown;

    this.toggleButton.textContent = recording
      ? "Stop & save"
      : counting
        ? "Cancel"
        : "Start recording";
    this.toggleButton.setAttribute("aria-pressed", String(recording));
    this.markButton.disabled = !recording;

    this.countdown.hidden = !counting;
    if (counting) {
      this.countdownNumber.textContent = String(this.remaining);
    }

    this.indicator.hidden = !recording;
    document.body.classList.toggle("is-recording", recording);
    if (recording) {
      const total = Math.floor((performance.now() - this.startedAt) / 1000);
      const mins = Math.floor(total / 60);
      const secs = String(total % 60).padStart(2, "0");
      // Saying so is the honest version of a frozen counter: the clock proves
      // recording is live, and this says nothing is being captured into it yet.
      const waiting = this.recorder.frames.length === 0 ? " · no pose" : "";
      this.indicatorTime.textContent = `${mins}:${secs}${waiting}`;
    }

    if (recording) {
      const mb = (this.recorder.estimateBytes() / 1048576).toFixed(1);
      this.status.textContent =
        `● ${this.recorder.durationSeconds.toFixed(0)}s · ` +
        `${this.recorder.frames.length} frames · ` +
        `${this.recorder.marks.length} marks · ~${mb}MB`;
    } else if (counting) {
      this.status.textContent = `Starting in ${this.remaining}\u2026 tap Cancel to stop`;
    } else if (this.lastSaved) {
      this.status.textContent = this.lastSaved;
    } else {
      this.status.textContent = "Ready — press Start, then Mark once per rep";
    }
  }

  /** Shows what the finished session contained, so a bad take is obvious. */
  reportSaved(filename, bytes, frameCount, markCount, fps) {
    this.lastSaved =
      `Saved ${filename} · ${frameCount} frames @ ${fps.toFixed(0)}fps · ` +
      `${markCount} marks · ${(bytes / 1048576).toFixed(1)}MB`;
    this.render();
  }
}

function isTypingTarget(target) {
  if (target == null) {
    return false;
  }
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
}
